import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { channels, RealtimeEventName, type MetricsSnapshot } from '@outfalak/shared'
import { api, ApiError } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'
import { useRealtimeEvent } from '../hooks/useRealtime.js'
import { ago, count as fmtCount } from '../lib/format.js'
import { Card, Empty, PageHead, RowsSkeleton, Segmented, Stat, StatsSkeleton, Ticks } from '../components/ui.js'
import { Donut, Funnel, LineChart } from '../components/charts.js'
import { IconByName } from '../components/iconByName.js'
import { ArrowRightIcon, BagIcon, CartIcon, DownloadIcon, SparkIcon, StoreIcon } from '../components/icons.js'

interface Kpi {
  label: string
  value: string
  icon: string
  tone: string
  delta: number | null
  upIsGood: boolean
}

interface DashboardPayload {
  range: string
  rangeLabel: string
  comparedTo: string
  ranges: Record<string, { label: string; days: number; bucket: string }>
  kpis: Kpi[]
  series: { labels: string[]; revenue: number[]; orders: number[]; maxRevenue: number; maxOrders: number }
  funnel: Array<{ label: string; count: number; pct: number }>
  paymentMethods: { slices: Array<{ key: string; label: string; count: number; pct: number; color: string }>; total: number }
  abandoned: { rows: Array<{ name: string; phone: string | null; value: string; when: string; verified: boolean }>; count: number; value: string }
  activity: Array<{ at: string; ago: string; icon: string; tone: string; title: string; meta: string }>
}

export function DashboardPage(): React.ReactNode {
  const { shop, user, shops, switchShop } = useAuth()
  const queryClient = useQueryClient()
  const [range, setRange] = useState('7d')
  const [live, setLive] = useState<MetricsSnapshot | null>(null)
  const [switchingId, setSwitchingId] = useState<string | null>(null)
  const [switchError, setSwitchError] = useState<string | null>(null)

  const { data, isPending, error } = useQuery({
    queryKey: ['dashboard', shop?.id, range],
    queryFn: () => api<DashboardPayload>('/v1/dashboard', { query: { range } }),
    enabled: Boolean(shop?.id),
  })

  const metricsChannel = shop ? channels.shopMetrics(shop.id) : null
  const ordersChannel = shop ? channels.shopOrders(shop.id) : null

  // A tick carries the small numbers, so the tiles move immediately. The heavy
  // parts of the screen — the chart series, the funnel — are refetched instead
  // of pushed, because sending a year of buckets down a socket on every order
  // costs more than the freshness is worth.
  useRealtimeEvent(metricsChannel, RealtimeEventName.MetricsTick, (snapshot) => setLive(snapshot))

  useRealtimeEvent(ordersChannel, RealtimeEventName.OrderCreated, () => {
    void queryClient.invalidateQueries({ queryKey: ['dashboard', shop?.id] })
    void queryClient.invalidateQueries({ queryKey: ['orders'] })
  })

  if (!shop) {
    return (
      <Card>
        <Empty
          icon={<StoreIcon size={24} />}
          title="No store selected"
          action={
            <Link to="/integrations" className="btn btn--primary">
              Connect a store
            </Link>
          }
        >
          Connect a Shopify, WooCommerce or custom storefront to start seeing orders and revenue here.
        </Empty>
      </Card>
    )
  }

  // The session holds no store (or holds one this account can no longer open)
  // while `/me` still returned a store — the stale-session divergence the API
  // now heals. Never show that 403 as a failed dashboard; show the picker the
  // message is asking for.
  const needsStoreChoice = error instanceof ApiError && error.status === 403
  if (needsStoreChoice) {
    const choose = (shopId: string): void => {
      setSwitchingId(shopId)
      setSwitchError(null)
      void switchShop(shopId)
        .catch((cause: unknown) => {
          setSwitchError(cause instanceof Error ? cause.message : 'Could not switch store.')
        })
        .finally(() => setSwitchingId(null))
    }

    return (
      <>
        <PageHead title="Choose a store" subtitle="Pick which store this dashboard shows." />
        <Card>
          <Empty icon={<StoreIcon size={24} />} title="Choose a store before opening this screen">
            Your sign-in is working, but no store is selected for it yet. Pick one below — the dashboard loads right after.
          </Empty>
          {shops.length > 0 ? (
            <ul className="list">
              {shops.map((option) => (
                <li key={option.id}>
                  <span className="avatar avatar--sm avatar--muted">
                    {(option.name ?? option.domain).slice(0, 1).toUpperCase()}
                  </span>
                  <span className="list__text">
                    <strong>{option.name ?? option.domain}</strong>
                    <small>{option.domain}</small>
                  </span>
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    disabled={switchingId === option.id}
                    onClick={() => choose(option.id)}
                  >
                    {switchingId === option.id ? 'Opening…' : option.id === shop.id ? 'Retry' : 'Open'}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ textAlign: 'center' }}>
              <Link to="/integrations" className="btn btn--primary">
                Connect a store
              </Link>
            </p>
          )}
          {switchError && (
            <p className="notice notice--err" role="alert" style={{ marginTop: 12 }}>
              {switchError}
            </p>
          )}
        </Card>
      </>
    )
  }

  const greeting = user?.name ? `Welcome back, ${user.name.split(' ')[0]}` : 'Dashboard'

  return (
    <>
      <PageHead
        title={greeting}
        subtitle={data ? `${data.rangeLabel} · ${data.comparedTo}` : 'Loading your store performance…'}
        actions={
          <>
            {data && (
              <Segmented
                label="Date range"
                value={data.range}
                onChange={setRange}
                options={Object.entries(data.ranges).map(([key, meta]) => ({ key, label: meta.label }))}
              />
            )}
            <button type="button" className="btn" onClick={() => window.print()}>
              <DownloadIcon size={16} />
              Export
            </button>
          </>
        }
      />

      {live && (
        <p className="notice notice--ok" role="status">
          <SparkIcon size={17} />
          <span className="notice__body">
            <strong>Live right now</strong>
            {fmtCount(live.orders)} orders · {live.revenue} · {live.conversionRate}% conversion
          </span>
        </p>
      )}

      {error && (
        <Card>
          <Empty error title="Could not load your dashboard">
            {(error as Error).message}
          </Empty>
        </Card>
      )}

      {isPending && <StatsSkeleton />}

      {data && (
        <>
          <section className="grid grid--4">
            {data.kpis.map((kpi) => (
              <Stat
                key={kpi.label}
                label={kpi.label}
                value={kpi.value}
                tone={kpi.tone}
                delta={kpi.delta}
                upIsGood={kpi.upIsGood}
                icon={<IconByName name={kpi.icon} />}
                footnote={<span className="faint">vs previous period</span>}
              />
            ))}
          </section>

          <div className="grid grid--main">
            <Card
              title="Revenue &amp; orders"
              subtitle={data.rangeLabel}
              action={
                <Segmented
                  label="Date range"
                  value={data.range}
                  onChange={setRange}
                  options={Object.entries(data.ranges).map(([key, meta]) => ({ key, label: meta.label }))}
                />
              }
            >
              <LineChart
                labels={data.series.labels}
                series={[
                  { key: 'revenue', label: 'Revenue (₹)', color: 'var(--brand)', values: data.series.revenue },
                  { key: 'orders', label: 'Orders', color: 'var(--ok)', values: data.series.orders, dashed: true },
                ]}
              />
            </Card>

            <Card title="Checkout funnel" subtitle="Where shoppers drop off">
              {data.funnel.length === 0 ? (
                <p className="muted">No checkout sessions in this range.</p>
              ) : (
                <Funnel stages={data.funnel} />
              )}
            </Card>
          </div>

          <div className="grid grid--2">
            <Card title="Revenue by payment method">
              {data.paymentMethods.total === 0 ? (
                <p className="muted">No settled orders in this range.</p>
              ) : (
                <Donut
                  centreValue={fmtCount(data.paymentMethods.total)}
                  centreLabel="settled orders"
                  slices={data.paymentMethods.slices.map((slice) => ({
                    key: slice.key,
                    label: slice.label,
                    value: slice.count,
                    color: slice.color,
                  }))}
                />
              )}
            </Card>

            <Card
              title="Abandoned checkouts"
              subtitle={`${fmtCount(data.abandoned.count)} worth ${data.abandoned.value}`}
              flush
              action={
                <Link to="/engagement/abandoned" className="btn btn--link">
                  Recover
                  <ArrowRightIcon size={15} />
                </Link>
              }
            >
              {data.abandoned.rows.length === 0 ? (
                <Empty icon={<CartIcon size={22} />} title="Nothing to recover">
                  Every checkout in this range was completed. That is a good problem to have.
                </Empty>
              ) : (
                <ul className="list">
                  {data.abandoned.rows.map((row) => (
                    <li key={`${row.phone ?? 'guest'}-${row.when}`}>
                      <span className="avatar avatar--sm avatar--muted">{(row.name || 'G').slice(0, 1).toUpperCase()}</span>
                      <span className="list__text">
                        <strong>{row.name || 'Guest'}</strong>
                        <small>
                          {row.phone ?? 'no phone'} · {row.verified ? 'verified' : 'unverified'}
                        </small>
                      </span>
                      <span className="list__value">{row.value}</span>
                      <small className="faint nowrap">{row.when}</small>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <div className="grid grid--main">
            <Card title="Recent activity" flush>
              {data.activity.length === 0 ? (
                <Empty icon={<BagIcon size={22} />} title="Nothing yet today">
                  Orders, refunds and store events will appear here as they happen.
                </Empty>
              ) : (
                <ul className="list">
                  {data.activity.map((event) => (
                    <li key={`${event.title}-${event.at}`}>
                      <span
                        className="stat__icon"
                        style={{ ['--tone' as string]: event.tone, width: 34, height: 34, marginBottom: 0 }}
                      >
                        <IconByName name={event.icon} size={16} />
                      </span>
                      <span className="list__text">
                        <strong>{event.title}</strong>
                        <small>{event.meta}</small>
                      </span>
                      <small className="faint nowrap">{event.ago || ago(event.at)}</small>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="Insights for you" accent>
              <Ticks
                brand
                items={[
                  `${data.rangeLabel}: ${data.kpis[0]?.value ?? '—'} in revenue across ${fmtCount(data.paymentMethods.total)} settled orders.`,
                  data.abandoned.count > 0
                    ? `${fmtCount(data.abandoned.count)} abandoned checkouts worth ${data.abandoned.value} are still recoverable.`
                    : 'No abandoned checkouts are waiting on recovery.',
                  'Enable WhatsApp reminders to recover more of what is left in carts.',
                ]}
              />
              <Link to="/analytics" className="btn btn--primary btn--block" style={{ marginTop: 16 }}>
                View detailed analytics
                <ArrowRightIcon size={16} />
              </Link>
            </Card>
          </div>
        </>
      )}

      {isPending && (
        <Card title="Recent activity" flush>
          <RowsSkeleton />
        </Card>
      )}
    </>
  )
}
